/**
 * Barrier - Numbered obstacle used by the vertical slice loop.
 *
 * Spawn-time stats are immutable after creation to keep DDA invisible.
 */

import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT } from '../config';
import { getParticlePool } from '../systems/ParticlePool';

export interface BarrierConfig {
  x: number;
  y: number;
  baseHp: number;
  finalHp: number;
  rewardSoldiers: number;
  rewardScore: number;
  width?: number;
}

export class Barrier extends Phaser.GameObjects.Container {
  static readonly DEFAULT_WIDTH = 92;
  static readonly HEIGHT = 72;
  /** Visual style lock for the current vertical slice art direction. */
  static readonly VISUAL_STYLE_LOCK = 'slice-v1-final';

  readonly spawnBaseHp: number;
  readonly spawnDdaMultiplier: number;
  readonly spawnFinalHp: number;
  readonly statsLocked: true = true;

  private readonly rewardSoldiers: number;
  private readonly rewardScore: number;
  private readonly barrierWidth: number;

  private hp: number;
  private isActive: boolean = true;
  private scrollSpeed: number = 95;

  private shadow: Phaser.GameObjects.Graphics;
  private frameSprite: Phaser.GameObjects.Image | null = null;
  private background: Phaser.GameObjects.Graphics;
  private highlight: Phaser.GameObjects.Graphics;
  private crackLayer: Phaser.GameObjects.Graphics;
  private hpText: Phaser.GameObjects.Text;
  private hitTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene, config: BarrierConfig) {
    super(scene, config.x, config.y);
    this.setData('visualStyleLock', Barrier.VISUAL_STYLE_LOCK);

    this.spawnBaseHp = config.baseHp;
    this.spawnFinalHp = Math.max(1, Math.round(config.finalHp));
    this.spawnDdaMultiplier = this.spawnFinalHp / Math.max(1, this.spawnBaseHp);
    this.hp = this.spawnFinalHp;
    this.rewardSoldiers = config.rewardSoldiers;
    this.rewardScore = config.rewardScore;
    this.barrierWidth = Math.max(70, config.width ?? Barrier.DEFAULT_WIDTH);

    this.shadow = scene.add.graphics();
    this.background = scene.add.graphics();
    this.highlight = scene.add.graphics();
    this.crackLayer = scene.add.graphics();
    this.hpText = scene.add.text(0, 0, String(this.hp), {
      fontFamily: FONTS.display,
      fontSize: '28px',
      color: '#FFFFFF',
      fontStyle: 'bold',
      stroke: '#1A1A1A',
      strokeThickness: 4,
    }).setOrigin(0.5);

    if (scene.textures.exists('gate-panel')) {
      this.frameSprite = scene.add.image(0, 0, 'gate-panel');
      this.frameSprite.setDisplaySize(this.barrierWidth + 20, Barrier.HEIGHT + 22);
      this.frameSprite.setTint(0x5A341E);
      this.frameSprite.setAlpha(0.9);
    }

    this.add(this.shadow);
    if (this.frameSprite) {
      this.add(this.frameSprite);
    }
    this.add(this.background);
    this.add(this.crackLayer);
    this.add(this.highlight);
    this.add(this.hpText);

    this.drawBody(1);

    scene.add.existing(this);
  }

  setScrollSpeed(speed: number): void {
    this.scrollSpeed = speed;
  }

  update(_time: number, delta: number): void {
    if (!this.isActive) return;

    const movement = (this.scrollSpeed * delta) / 1000;
    this.y += movement;

    if (this.y > GAME_HEIGHT + Barrier.HEIGHT) {
      this.destroyBarrier(false);
    }
  }

  takeDamage(damage: number): boolean {
    if (!this.isActive) return false;

    this.hp = Math.max(0, this.hp - Math.max(1, Math.round(damage)));
    const hpRatio = this.hp / this.spawnFinalHp;
    this.drawBody(hpRatio);
    this.playHitPulse();
    this.hpText.setText(String(this.hp));

    if (this.hp <= 0) {
      this.destroyBarrier(true);
      return true;
    }

    return false;
  }

  /**
   * Collision fallback when player touches a still-active barrier.
   * Returns the number of soldiers to remove from the army.
   */
  resolveArmyCollision(currentArmySize: number): number {
    if (!this.isActive) return 0;

    const soldiersLost = Math.min(currentArmySize, Math.max(1, Math.ceil(this.hp / 6)));
    this.destroyBarrier(false);
    return soldiersLost;
  }

  private drawBody(hpRatio: number): void {
    const ratio = Phaser.Math.Clamp(hpRatio, 0, 1);
    const halfW = this.barrierWidth / 2;
    const halfH = Barrier.HEIGHT / 2;
    const radius = 10;

    const fillColor = ratio > 0.6 ? 0x7B4A2D : ratio > 0.3 ? 0x8B3629 : 0x5D2222;

    this.shadow.clear();
    this.shadow.fillStyle(0x000000, 0.2);
    this.shadow.fillEllipse(0, halfH + 9, this.barrierWidth * 0.84, 14);

    this.background.clear();
    this.background.fillStyle(fillColor, 1);
    this.background.fillRoundedRect(-halfW, -halfH, this.barrierWidth, Barrier.HEIGHT, radius);
    this.background.lineStyle(3, 0x2C1D13, 0.95);
    this.background.strokeRoundedRect(-halfW, -halfH, this.barrierWidth, Barrier.HEIGHT, radius);

    this.background.lineStyle(3, 0x000000, 0.16);
    const stripeTop = -halfH + 14;
    const stripeBottom = halfH - 8;
    for (let stripeX = -halfW + 10; stripeX < halfW - 10; stripeX += 16) {
      this.background.beginPath();
      this.background.moveTo(stripeX, stripeTop);
      this.background.lineTo(stripeX + 10, stripeBottom);
      this.background.strokePath();
    }

    this.highlight.clear();
    this.highlight.fillStyle(0xFFFFFF, 0.18);
    this.highlight.fillRoundedRect(-halfW + 7, -halfH + 6, this.barrierWidth - 14, 10, 4);

    this.crackLayer.clear();
    if (ratio < 0.58) {
      const crackAlpha = Phaser.Math.Linear(0.2, 0.7, 1 - ratio);
      this.crackLayer.lineStyle(2, 0x22100A, crackAlpha);
      this.crackLayer.beginPath();
      this.crackLayer.moveTo(-halfW + 20, -4);
      this.crackLayer.lineTo(-8, 10);
      this.crackLayer.lineTo(8, 2);
      this.crackLayer.lineTo(halfW - 18, 14);
      this.crackLayer.strokePath();
    }
  }

  private playHitPulse(): void {
    if (this.hitTween) {
      this.hitTween.stop();
      this.hitTween = null;
    }

    this.setScale(1);
    this.hitTween = this.scene.tweens.add({
      targets: this,
      scaleX: 1.06,
      scaleY: 1.06,
      duration: 70,
      yoyo: true,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.setScale(1);
        this.hitTween = null;
      },
    });
  }

  private spawnBreakBurst(): void {
    const pool = getParticlePool();
    if (!pool) return;

    pool.spawnBurst(this.x, this.y, 'square', COLORS.enemyDark, Phaser.Math.Between(8, 12), {
      minSize: 4,
      maxSize: 8,
      minVelocity: 80,
      maxVelocity: 170,
      minDuration: 180,
      maxDuration: 260,
      alpha: 0.75,
    });
  }

  private destroyBarrier(awarded: boolean): void {
    if (!this.isActive) return;

    this.isActive = false;
    this.emit('destroyed', {
      barrier: this,
      rewarded: awarded,
      rewardSoldiers: awarded ? this.rewardSoldiers : 0,
      rewardScore: awarded ? this.rewardScore : 0,
      spawnFinalHp: this.spawnFinalHp,
      hpLocked: this.statsLocked,
    });

    this.spawnBreakBurst();

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 110,
      ease: 'Power2',
      onComplete: () => this.destroy(),
    });
  }

  getCollisionBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.x - this.barrierWidth / 2,
      y: this.y - Barrier.HEIGHT / 2,
      width: this.barrierWidth,
      height: Barrier.HEIGHT,
    };
  }

  getCollisionRadius(): number {
    return Math.min(this.barrierWidth, Barrier.HEIGHT) * 0.45;
  }

  getHp(): number {
    return this.hp;
  }

  getIsActive(): boolean {
    return this.isActive;
  }

  getRewardSoldiers(): number {
    return this.rewardSoldiers;
  }

  getRewardScore(): number {
    return this.rewardScore;
  }

  destroy(fromScene?: boolean): void {
    if (this.hitTween) {
      this.hitTween.stop();
      this.hitTween = null;
    }
    super.destroy(fromScene);
  }
}
